import { useMemo } from 'react';
import { CallRouteType, PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import PhoneOverrideApi from '@frontend/api-overrides-v2';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../../query-keys';
import { logError } from '../../helpers/utils';
import { ExtendedCallRoute } from '../../types';

export interface UseCallRouteDataParams {
  callRouteId: string;
  tenantId: string;
  tenantLocationIds: string[];
  onCallRouteError?: () => void;
}

/**
 * Hook to fetch and manage all call route related data.
 * Groups all data fetching queries for the CallRouteDetails component.
 */
export const useCallRouteData = ({
  callRouteId,
  tenantId,
  tenantLocationIds,
  onCallRouteError,
}: UseCallRouteDataParams) => {
  const { t } = useTranslation('phone');
  const alert = useAlert();

  // Fetch call route data
  const { data: callRoute, isLoading: callRouteIsLoading } = CallRouteV1.Queries.useReadQuery({
    request: { callRouteId },
    options: {
      enabled: !!callRouteId,
      staleTime: 0,
      cacheTime: 0,
      onError: (error) => {
        alert.error(t('Failed to load call route.'));
        logError('Failed to fetch call route', 'useCallRouteData:useReadQuery', {
          error,
          callRouteId,
          tenantId,
        });
        onCallRouteError?.();
      },
    },
  });

  const isDeviceExtensionRoute = callRoute?.callRoute.callRouteType === CallRouteType.CALL_ROUTE_TYPE_EXTENSION;

  // Memoized default values to ensure stable references
  const defaultOverridesData = useMemo(() => ({ phoneOverrides: [], lastUsed: {}, deviceExtensionOverrides: [] }), []);
  const defaultEmptyArray = useMemo(() => [], []);

  // Fetch overrides
  const { data: overridesDataRaw, isLoading: phoneOverridesIsLoading } = useQuery({
    queryKey: [
      tenantId,
      ...queryKeys.settings.listOverrides({
        includeDeviceExtensionOverrides: true,
      }),
    ],
    queryFn: async () =>
      PhoneOverrideApi.List({
        tenantId: tenantId ?? '',
        includes: { deviceExtensionOverrides: true },
      }),
    retry: false,
    enabled: !!tenantId,
    onError: (error) => {
      alert.error(t('Failed to load overrides.'));
      logError('Failed to fetch overrides in call route details.', 'useCallRouteData:listOverrides', {
        error,
        callRouteId,
        tenantId,
      });
    },
  });
  // Use memoized default value to ensure stable reference
  const overridesData = overridesDataRaw ?? defaultOverridesData;

  // Combine phone overrides and device extension overrides for easier lookup
  const allOverridesArray = useMemo(
    () => [...overridesData.phoneOverrides, ...(overridesData.deviceExtensionOverrides ?? [])],
    [overridesData.phoneOverrides, overridesData.deviceExtensionOverrides]
  );

  // Fetch phone numbers for locations
  const callRouteLocationId = callRoute?.callRoute.locationId;
  const { data: allPhoneNumbersRaw, isLoading: phoneNumbersIsLoading } =
    PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
      request: { locationIds: tenantLocationIds, voice: true },
      options: {
        enabled: !!tenantLocationIds.length && !!callRouteLocationId,
        retry: false,
        onError: (error) => {
          alert.error(t('Failed to load location phone numbers.'));
          logError(
            'Failed to fetch location phone numbers in call route details.',
            'useCallRouteData:useListPhoneNumbersForLocationsQuery',
            {
              error,
              callRouteId,
              locationIds: tenantLocationIds,
              tenantId,
            }
          );
        },
      },
    });
  // Use memoized default value to ensure stable reference
  const allPhoneNumbers = allPhoneNumbersRaw?.phoneNumbers ?? defaultEmptyArray;

  // Fetch unallocated phone numbers
  const { data: unallocatedPhoneNumbersRaw, isLoading: unallocatedPhoneNumbersIsLoading } =
    CallRouteV1.Queries.useListUnallocatedPhoneNumbersQuery({
      request: { tenantId: tenantId ?? '' },
      options: {
        retry: false,
        enabled: !!tenantId,
        cacheTime: 1000,
        staleTime: 1000,
        onError: (error) => {
          alert.error(t('Failed to load unallocated phone numbers.'));
          logError(
            'Failed to fetch unallocated phone numbers in call route details.',
            'useCallRouteData:useListUnallocatedPhoneNumbersQuery',
            {
              error,
              callRouteId,
              tenantId,
            }
          );
        },
      },
    });
  const unallocatedPhoneNumbers = unallocatedPhoneNumbersRaw?.unallocatedPhoneNumbers ?? defaultEmptyArray;

  // Build extended call route with phone numbers and overrides
  const extendedCallRoute = useMemo<ExtendedCallRoute | undefined>(() => {
    if (!callRoute) {
      return undefined;
    }

    const callRouteData = callRoute.callRoute;
    if (!callRouteData) {
      return undefined;
    }

    const phoneNumberIds = callRouteData.phoneNumberIds ?? [];
    const phoneNumbersForCallRoute =
      allPhoneNumbers?.filter((phoneNumber) => phoneNumberIds.includes(phoneNumber.id)) ?? [];
    const phoneNumbers: PhoneNumber[] = phoneNumbersForCallRoute.map((phoneNumber) => ({
      id: phoneNumber.id,
      phoneNumber: String(phoneNumber.phoneNumber?.nationalNumber ?? ''),
      locationId: phoneNumber.locationId ?? '',
      displayName: phoneNumber.displayName ?? '',
    }));

    const callRoutePhoneOverride = allOverridesArray?.find(
      (phoneOverride) => phoneOverride.instructionSetId === callRouteData.rootInstructionSetId
    );

    return {
      ...callRouteData,
      phoneNumbers,
      callRoutePhoneOverride,
    };
  }, [callRoute, allPhoneNumbers, allOverridesArray]);

  const isLoading =
    callRouteIsLoading || phoneNumbersIsLoading || phoneOverridesIsLoading || unallocatedPhoneNumbersIsLoading;

  return {
    callRoute,
    callRouteIsLoading,
    isDeviceExtensionRoute,
    allPhoneNumbers,
    phoneNumbersIsLoading,
    unallocatedPhoneNumbers,
    unallocatedPhoneNumbersIsLoading,
    overridesData,
    phoneOverridesIsLoading,
    extendedCallRoute,
    isLoading,
  };
};
